const API_BASE_URL = `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api`;

export interface MultaPolicia {
  id?: number;
  folio: string;
  fechaInfraccion: string;
  horaInfraccion?: string;
  lugarInfraccion: string;
  latitud?: number | null;
  longitud?: number | null;
  
  // Datos de la infracción - Placa
  numeroPlaca: string;
  estadoEmisorPlaca: string;
  vigenciaPlaca?: string;
  tipoVehiculo: string;
  categoriaVehiculo?: string;
  tipoVehiculoCategoria?: string;
  marca?: string;
  modelo?: string;
  color?: string;
  
  // Datos del conductor
  nombreConductor?: string;
  apellidosConductor?: string;
  domicilioConductor?: string;
  telefonoConductor?: string;
  esPropietario?: boolean;
  
  // Datos del propietario (si es diferente)
  nombrePropietario?: string;
  apellidosPropietario?: string;
  domicilioPropietario?: string;
  telefonoPropietario?: string;
  
  // Infracción
  articuloViolado?: string;
  fraccion?: string;
  descripcionConducta?: string;
  
  // Garantía
  tipoGarantia?: string; // licencia, placa, vehiculo
  
  // Datos de licencia (si aplica)
  tipoLicencia?: string; // federal o estatal
  categoriaLicencia?: string; // A, B, C, D, E, F
  entidadEmisoraLicencia?: string;
  
  // Datos de placa como garantía
  placaGarantia?: string;
  usarMismaPlaca?: boolean;
  
  // Campos de aseguramiento
  numeroSerieVIN?: string;
  numeroMotor?: string;
  corralónAsignado?: string;
  ubicacionAseguramiento?: string;
  latitudAseguramiento?: number;
  longitudAseguramiento?: number;
  fechaAseguramiento?: string;
  horaAseguramiento?: string;
  observacionesAseguramiento?: string;
  
  // Información del oficial
  nombreOficial?: string;
  gradoOficial?: string;
  
  // Información financiera y administrativa
  monto: number;
  estatus: string;
  estatusCalificacion?: string;
  calificado?: boolean; // Si el juez municipal ya calificó y dictaminó el monto
  
  // Propiedades del servidor (mapeo directo)
  entidad?: string; // Estado emisor de placa
  modeloVehiculo?: string; // Modelo del vehículo
  ciudadano?: string; // Nombre completo del conductor
  descripcion?: string; // Descripción de la conducta
  corralon?: string; // Corralón asignado
  ordenGrua?: OrdenGrua[]; // Órdenes de grúa asociadas
  carroceria?: {
    id: number;
    codigo: string;
    tipo?: string;
    descripcion: string;
    categoria: string;
    activo: boolean;
    creadoEn: string;
  }; // Información de carrocería
  
  // Metadatos
  creadaEn?: string;
  actualizadaEn?: string;
}

export interface Evidencia {
  id?: number;
  url: string;
  tipo: string;
  descripcion?: string | null;
  multaId?: number;
  fechaSubida?: string;
}

export interface OrdenGrua {
  id?: number;
  url: string;
  tipo: string;
  aseguramientolugar?: string | null;
  aseguramientoLat?: string | null;
  aseguramientoLng?: string | null;
  observaciones?: string | null;
  multaId?: number;
  fechaHora?: string;
}

// Catálogos
export const ESTADOS_MEXICO = [
  'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas', 'Chihuahua',
  'Ciudad de México', 'Coahuila', 'Colima', 'Durango', 'Estado de México', 'Guanajuato',
  'Guerrero', 'Hidalgo', 'Jalisco', 'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca',
  'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa', 'Sonora', 'Tabasco',
  'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas'
];

export const TIPOS_VEHICULO = [
  'Sedán', 'Hatchback', 'Motocicleta', 'SUV', 'Coupé', 'Convertible', 'Pickup/Camioneta',
  'Minivan/Van', 'Camión', 'Camioneta de carga', 'Microbús', 'Autobús', 'Ciclomotor',
  'Motocross/Enduro', 'Tractocamión', 'Tráiler', 'Otro'
];

export const LICENCIAS_FEDERALES = [
  { codigo: 'A', descripcion: 'Tipo A: automóviles particulares' },
  { codigo: 'B', descripcion: 'Tipo B: carga ligera, sin articulación ni materiales peligrosos' },
  { codigo: 'C', descripcion: 'Tipo C: carga pesada de dos o más ejes, sin materiales peligrosos' },
  { codigo: 'D', descripcion: 'Tipo D: transporte turístico o chofer-guía' },
  { codigo: 'E', descripcion: 'Tipo E: tractocamiones articulados o con materiales peligrosos' },
  { codigo: 'F', descripcion: 'Tipo F: transporte de pasajeros a aeropuertos y puertos' }
];

export const LICENCIAS_ESTATALES = [
  { codigo: 'A', descripcion: 'Tipo A: transporte público (taxis, combis, autobuses locales)' },
  { codigo: 'B', descripcion: 'Tipo B: carga ligera o uso mixto (particular + carga)' },
  { codigo: 'C', descripcion: 'Tipo C: automóviles particulares' },
  { codigo: 'D', descripcion: 'Tipo D: motocicletas' },
  { codigo: 'F', descripcion: 'Tipo F: carga pesada o remolques' },
  { codigo: 'MENOR', descripcion: 'Permiso para menores: conducción de automóviles con restricciones' }
];

export const policiaMunicipalApi = {
  getMultas: async (): Promise<MultaPolicia[]> => {
    const response = await fetch(`${API_BASE_URL}/multas-transito`);
    if (!response.ok) throw new Error('Error al obtener multas');
    return response.json();
  },

  createMulta: async (multa: Omit<MultaPolicia, 'id'>): Promise<MultaPolicia> => {
    // Mapear carrocería a carroceriaId
    const carroceriaMap: { [key: string]: number } = {
      'Sedán': 1,
      'Hatchback': 2,
      'SUV': 3,
      'Pickup/Camioneta': 4,
      'Motocicleta': 5,
      'Camión': 6,
      'Autobús': 7
    };
    
    const multaFormateada = {
      folio: multa.folio,
      fechaInfraccion: multa.fechaInfraccion ? new Date(multa.fechaInfraccion).toISOString() : new Date().toISOString(),
      lugarInfraccion: multa.lugarInfraccion || null,
      latitud: multa.latitud ? multa.latitud.toString() : null,
      longitud: multa.longitud ? multa.longitud.toString() : null,
      numeroPlaca: multa.numeroPlaca,
      entidad: multa.estadoEmisorPlaca,
      vigenciaPlaca: multa.vigenciaPlaca || null,
      carroceriaId: carroceriaMap[multa.tipoVehiculo] || 1,
      tipoVehiculo: multa.tipoVehiculoCategoria || 'Particular',
      marca: multa.marca || null,
      modeloVehiculo: multa.modelo || null,
      color: multa.color || null,
      ciudadano: multa.nombreConductor && multa.apellidosConductor ? `${multa.nombreConductor} ${multa.apellidosConductor}` : null,
      domicilioConductor: multa.domicilioConductor || null,
      telefonoConductor: multa.telefonoConductor || null,
      propietarioVehiculo: multa.esPropietario ? 
        (multa.nombreConductor && multa.apellidosConductor ? `${multa.nombreConductor} ${multa.apellidosConductor}` : null) :
        (multa.nombrePropietario && multa.apellidosPropietario ? `${multa.nombrePropietario} ${multa.apellidosPropietario}` : null),
      articuloViolado: multa.articuloViolado || null,
      fraccion: multa.fraccion || null,
      descripcion: multa.descripcionConducta || null,
      tipoGarantia: multa.tipoGarantia || null,
      numeroSerieVIN: multa.numeroSerieVIN || null,
      numeroMotor: multa.numeroMotor || null,
      corralon: multa.corralónAsignado || null,
      nombreOficial: multa.nombreOficial || null,
      gradoOficial: multa.gradoOficial || null,
      monto: multa.monto ? multa.monto.toString() : '0',
      estatus: multa.estatus || 'PENDIENTE',
      calificado: false
    };
    
    console.log('Datos enviados al servidor:', multaFormateada);
    
    const response = await fetch(`${API_BASE_URL}/multas-transito`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(multaFormateada),
    });
    
    if (!response.ok) {
      let errorMessage = `Error ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch (e) {
        // Si no se puede parsear el JSON, usar el mensaje por defecto
      }
      throw new Error(errorMessage);
    }
    return response.json();
  },

  // Descargar orden de grúa
  downloadOrdenGrua: async (multaId: number, ordenId: number): Promise<Blob> => {
    const response = await fetch(`${API_BASE_URL}/multas-transito/${multaId}/orden-grua/${ordenId}/download`);
    if (!response.ok) throw new Error('Error al descargar orden de grúa');
    return response.blob();
  },

  // Actualizar multa
  updateMulta: async (id: number, multa: Partial<MultaPolicia>): Promise<MultaPolicia> => {
    const multaFormateada = {
      folio: multa.folio,
      fechaInfraccion: multa.fechaInfraccion ? new Date(multa.fechaInfraccion).toISOString() : undefined,
      lugarInfraccion: multa.lugarInfraccion,
      latitud: multa.latitud ? multa.latitud.toString() : undefined,
      longitud: multa.longitud ? multa.longitud.toString() : undefined,
      numeroPlaca: multa.numeroPlaca,
      entidad: multa.estadoEmisorPlaca,
      vigenciaPlaca: multa.vigenciaPlaca,
      tipoVehiculo: multa.tipoVehiculoCategoria || 'Particular',
      marca: multa.marca,
      modeloVehiculo: multa.modelo,
      color: multa.color,
      ciudadano: multa.nombreConductor && multa.apellidosConductor ? `${multa.nombreConductor} ${multa.apellidosConductor}` : undefined,
      domicilioConductor: multa.domicilioConductor,
      telefonoConductor: multa.telefonoConductor,
      propietarioVehiculo: multa.esPropietario ? 
        (multa.nombreConductor && multa.apellidosConductor ? `${multa.nombreConductor} ${multa.apellidosConductor}` : undefined) :
        (multa.nombrePropietario && multa.apellidosPropietario ? `${multa.nombrePropietario} ${multa.apellidosPropietario}` : undefined),
      articuloViolado: multa.articuloViolado,
      fraccion: multa.fraccion,
      descripcion: multa.descripcionConducta,
      tipoGarantia: multa.tipoGarantia,
      numeroSerieVIN: multa.numeroSerieVIN,
      numeroMotor: multa.numeroMotor,
      corralon: multa.corralónAsignado,
      nombreOficial: multa.nombreOficial,
      gradoOficial: multa.gradoOficial,
      monto: multa.monto ? multa.monto.toString() : undefined,
      estatus: multa.estatus,
      calificado: multa.calificado
    };
    
    // Remover campos undefined
    Object.keys(multaFormateada).forEach(key => {
      if (multaFormateada[key as keyof typeof multaFormateada] === undefined) {
        delete multaFormateada[key as keyof typeof multaFormateada];
      }
    });
    
    const response = await fetch(`${API_BASE_URL}/multas-transito/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(multaFormateada),
    });
    
    if (!response.ok) throw new Error('Error al actualizar multa');
    return response.json();
  },

  // Eliminar multa
  deleteMulta: async (id: number): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/multas-transito/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Error al eliminar multa');
  },

  // Obtener multa por ID
  getMultaById: async (id: number): Promise<MultaPolicia> => {
    const response = await fetch(`${API_BASE_URL}/multas-transito/${id}`);
    if (!response.ok) throw new Error('Error al obtener multa');
    return response.json();
  }
};